// Tweaks.jsx — Floating settings panel (language, font, mode, animations, viewport)
import React from "react";
import PixelIcons from "./PixelIcons.jsx";
import { useTweaks } from "../hooks/useTweaks.js";
import { useT } from "../i18n/useT.js";

const { Diamond } = PixelIcons;

const panelStyles = {
  panel: {
    position: "fixed", top: 100, right: 24, width: 320,
    background: "var(--ragross-purple-night)",
    border: "2px solid var(--ragross-magenta)",
    boxShadow: "8px 8px 0 var(--ragross-cyan), 0 30px 60px rgba(0,0,0,0.6)",
    borderRadius: 4, zIndex: 998,
    fontFamily: "'Press Start 2P', monospace", color: "#fff", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px",
    background: "linear-gradient(90deg, var(--ragross-magenta), var(--ragross-purple))",
    borderBottom: "2px solid var(--ragross-cyan)", userSelect: "none",
  },
  title: { fontSize: 11, letterSpacing: "0.18em", color: "#fff", display: "flex", alignItems: "center", gap: 8 },
  close: {
    width: 24, height: 24, background: "var(--ragross-purple-night)",
    border: "1px solid #fff", color: "#fff", fontSize: 12,
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", lineHeight: 1,
  },
  body: { padding: 16, display: "flex", flexDirection: "column", gap: 18 },
  section: { display: "flex", flexDirection: "column", gap: 8 },
  label: {
    fontSize: 9, letterSpacing: "0.16em", color: "var(--ragross-cyan)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  segWrap: {
    display: "flex", border: "1px solid var(--border-soft)", borderRadius: 2,
    background: "var(--ragross-purple-deep)", overflow: "hidden",
  },
  seg: {
    flex: 1, padding: "10px 8px", fontSize: 9, letterSpacing: "0.10em", textAlign: "center",
    background: "transparent", border: "none", color: "var(--fg-3)", cursor: "pointer",
    transition: "background 200ms, color 200ms", fontFamily: "'Press Start 2P', monospace",
  },
  segActive: { background: "var(--ragross-magenta)", color: "#fff", boxShadow: "inset 0 -2px 0 var(--ragross-cyan)" },
  konamiHint: {
    fontFamily: "'VT323', monospace", fontSize: 14, color: "var(--ragross-gold)",
    textAlign: "center", letterSpacing: "0.05em", padding: "10px 12px",
    border: "1px dashed rgba(253,177,21,0.4)", borderRadius: 2, background: "rgba(253,177,21,0.04)",
  },
};

function Segment({ value, options, onChange }) {
  return (
    <div style={panelStyles.segWrap}>
      {options.map((o) => (
        <button
          key={o.value}
          style={{ ...panelStyles.seg, ...(value === o.value ? panelStyles.segActive : {}) }}
          onClick={() => onChange(o.value)}
        >{o.label}</button>
      ))}
    </div>
  );
}

export default function Tweaks() {
  const [s, setKey] = useTweaks();
  const t = useT();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <button
        className="tweaks-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Tweaks"
        title={t("tw.title")}
      >
        ▸
      </button>

      {open && (
        <aside style={panelStyles.panel}>
          <header style={panelStyles.header}>
            <span style={panelStyles.title}>
              <Diamond size={11} />
              {t("tw.title")}
            </span>
            <button style={panelStyles.close} onClick={() => setOpen(false)} aria-label="close">×</button>
          </header>
          <div style={panelStyles.body}>
            <div style={panelStyles.section}>
              <span style={panelStyles.label}><span>{t("tw.lang")}</span><span style={{ color: "var(--fg-3)" }}>ES / EN</span></span>
              <Segment value={s.lang} onChange={(v) => setKey("lang", v)}
                options={[{ value: "es", label: "ESPAÑOL" }, { value: "en", label: "ENGLISH" }]} />
            </div>
            <div style={panelStyles.section}>
              <span style={panelStyles.label}><span>{t("tw.display")}</span></span>
              <Segment value={s.displayFont} onChange={(v) => setKey("displayFont", v)}
                options={[{ value: "space", label: t("tw.fontSpace") }, { value: "press", label: t("tw.fontPress") }]} />
            </div>
            <div style={panelStyles.section}>
              <span style={panelStyles.label}><span>{t("tw.mode")}</span></span>
              <Segment value={s.mode} onChange={(v) => setKey("mode", v)}
                options={[{ value: "arcade", label: t("tw.modeArcade") }, { value: "pro", label: t("tw.modePro") }]} />
            </div>
            <div style={panelStyles.section}>
              <span style={panelStyles.label}><span>{t("tw.anim")}</span></span>
              <Segment value={s.animations ? "on" : "off"} onChange={(v) => setKey("animations", v === "on")}
                options={[{ value: "on", label: t("tw.on") }, { value: "off", label: t("tw.off") }]} />
            </div>
            <div style={panelStyles.section}>
              <span style={panelStyles.label}><span>{t("tw.viewport")}</span></span>
              <Segment value={s.viewport} onChange={(v) => setKey("viewport", v)}
                options={[{ value: "desktop", label: t("tw.vpDesktop") }, { value: "mobile", label: t("tw.vpMobile") }]} />
            </div>
            <div style={panelStyles.konamiHint}>
              <span style={{ color: "var(--ragross-magenta)" }}>▸ </span>
              {t("tw.konami")}
            </div>
          </div>
        </aside>
      )}
    </>
  );
}
